import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Plus, Trash2, Save, ImagePlus, Users, Mail, Phone, MessageSquare, RefreshCw } from "lucide-react";
import {
  verifyAdmin, setAdminKey, getAdminKey, clearAdminKey,
  getStories, createStory, updateStory, deleteStory,
  getGallery, addGalleryImage, deleteGalleryImage,
  listRsvps, getMessages,
} from "@/lib/api";
import axios from "axios";
import { API } from "@/lib/api";

const tabs = [
  { id: "stories", label: "Story Timeline" },
  { id: "gallery", label: "Gallery" },
  { id: "rsvps", label: "RSVPs" },
  { id: "messages", label: "Messages" },
];

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [tab, setTab] = useState("stories");

  useEffect(() => {
    const k = getAdminKey();
    if (k) {
      verifyAdmin(k)
        .then(() => setAuthed(true))
        .catch(() => clearAdminKey());
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      await verifyAdmin(keyInput);
      setAdminKey(keyInput);
      setAuthed(true);
    } catch {
      setAuthError("Invalid admin key");
    }
  };

  const logout = () => {
    clearAdminKey();
    setAuthed(false);
    navigate("/admin");
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16" data-testid="admin-login">
        <form onSubmit={login} className="glass-card p-8 md:p-10 w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold">
              <Lock size={18} />
            </div>
            <div>
              <p className="text-[0.6rem] uppercase tracking-[0.4em] text-gold">Admin</p>
              <h1 className="font-serif text-3xl text-ivory">#PandA Studio</h1>
            </div>
          </div>
          <label className="text-[0.65rem] uppercase tracking-[0.32em] text-ivory-dim mb-2 block">
            Admin Key
          </label>
          <input
            data-testid="admin-key-input"
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Enter admin key"
            className="w-full bg-ink-900/60 border border-gold/25 focus:border-gold/70 rounded-xl px-4 py-3 text-ivory outline-none"
          />
          {authError && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-burgundy-glow">
              {authError}
            </p>
          )}
          <button type="submit" className="btn-gold w-full justify-center mt-6" data-testid="admin-login-submit">
            Enter Studio
          </button>
          <p className="mt-4 text-xs text-ivory-dim italic">
            Default key is set via <code>ADMIN_KEY</code> in <code>backend/.env</code>.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 px-5 md:px-10 min-h-screen" data-testid="admin-page">
      <header className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.4em] text-gold">Admin</p>
          <h1 className="font-serif text-4xl text-ivory">#PandA Studio</h1>
        </div>
        <button onClick={logout} className="btn-ghost-gold" data-testid="admin-logout">
          <LogOut size={14} /> Logout
        </button>
      </header>

      <nav className="max-w-6xl mx-auto mb-8 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-2.5 rounded-full text-[0.7rem] uppercase tracking-[0.28em] border transition ${
              tab === t.id
                ? "border-gold text-gold bg-burgundy/30"
                : "border-gold/20 text-ivory-muted hover:border-gold/50 hover:text-ivory"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="max-w-6xl mx-auto">
        {tab === "stories" && <StoriesPanel />}
        {tab === "gallery" && <GalleryPanel />}
        {tab === "rsvps" && <RsvpsPanel />}
        {tab === "messages" && <MessagesPanel />}
      </div>
    </div>
  );
}

/* ---------- Panels ---------- */

function StoriesPanel() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await getStories()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addNew = async () => {
    setSaving("new");
    try {
      const created = await createStory({
        year: "YYYY", title: "New Chapter", body: "Write your story…",
        image: "https://images.unsplash.com/photo-1708127851887-98a0c1d9c677?auto=format&fit=crop&w=1200",
        order: items.length,
      });
      setItems([...items, created]);
    } finally { setSaving(""); }
  };

  const save = async (item) => {
    setSaving(item.id);
    try {
      const updated = await updateStory(item.id, {
        year: item.year, title: item.title, body: item.body,
        image: item.image, order: item.order,
      });
      setItems(items.map((x) => (x.id === item.id ? updated : x)));
    } finally { setSaving(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this chapter?")) return;
    await deleteStory(id);
    setItems(items.filter((x) => x.id !== id));
  };

  const change = (id, key) => (e) =>
    setItems(items.map((x) => (x.id === id ? { ...x, [key]: key === "order" ? Number(e.target.value) : e.target.value } : x)));

  if (loading) return <p className="text-ivory-muted">Loading…</p>;

  return (
    <div data-testid="admin-stories-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-ivory">Story Timeline</h2>
        <button onClick={addNew} disabled={saving === "new"} className="btn-gold" data-testid="admin-story-add">
          <Plus size={14} /> Add chapter
        </button>
      </div>
      <div className="space-y-5">
        {items.map((it, i) => (
          <div key={it.id} className="glass-card p-6" data-testid={`admin-story-${i}`}>
            <div className="grid md:grid-cols-[120px_1fr_120px] gap-4 items-start">
              <input value={it.year} onChange={change(it.id, "year")} placeholder="Year" className="bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory" />
              <input value={it.title} onChange={change(it.id, "title")} placeholder="Title" className="bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory" />
              <input type="number" value={it.order} onChange={change(it.id, "order")} placeholder="Order" className="bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory" />
            </div>
            <textarea value={it.body} onChange={change(it.id, "body")} rows={3} placeholder="Body" className="mt-3 w-full bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory resize-none" />
            <input value={it.image} onChange={change(it.id, "image")} placeholder="Image URL" className="mt-3 w-full bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory" />
            <div className="flex items-center justify-end gap-3 mt-4">
              <button onClick={() => remove(it.id)} className="btn-ghost-gold" data-testid={`admin-story-delete-${i}`}>
                <Trash2 size={13} /> Delete
              </button>
              <button onClick={() => save(it)} disabled={saving === it.id} className="btn-gold" data-testid={`admin-story-save-${i}`}>
                <Save size={13} /> {saving === it.id ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => setItems(await getGallery());
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setAdding(true);
    try {
      const created = await addGalleryImage({ url, caption: caption || null, order: items.length });
      setItems([...items, created]);
      setUrl(""); setCaption("");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this image?")) return;
    await deleteGalleryImage(id);
    setItems(items.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="admin-gallery-panel">
      <h2 className="font-serif text-2xl text-ivory mb-4">Gallery</h2>
      <form onSubmit={add} className="glass-card p-5 mb-6 grid md:grid-cols-[1fr_200px_auto] gap-3 items-center">
        <input
          value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://image-url.jpg"
          className="bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory"
          data-testid="admin-gallery-url"
        />
        <input
          value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)"
          className="bg-ink-900/60 border border-gold/25 rounded-lg px-3 py-2 text-ivory"
          data-testid="admin-gallery-caption"
        />
        <button type="submit" disabled={adding} className="btn-gold" data-testid="admin-gallery-add">
          <ImagePlus size={14} /> {adding ? "Adding…" : "Add"}
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((g, i) => (
          <div key={g.id} className="relative group rounded-xl overflow-hidden border border-gold/20" data-testid={`admin-gallery-${i}`}>
            <img src={g.url} alt={g.caption || "gallery"} className="w-full aspect-square object-cover" />
            <button
              onClick={() => remove(g.id)}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ink-900/80 border border-gold/40 text-burgundy-glow hover:text-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              aria-label="Delete"
              data-testid={`admin-gallery-delete-${i}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RsvpsPanel() {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRsvps(await listRsvps()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    const headers = ["Name", "Email", "Phone", "Attending", "Guests", "Event", "Note", "Date"];
    const rows = rsvps.map((r) => [
      r.name, r.email || "", r.phone || "", r.attending, r.guests, r.event, (r.note || "").replace(/\n/g, " "), r.created_at,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "panda_rsvps.csv";
    link.click();
  };

  const counts = rsvps.reduce(
    (acc, r) => {
      acc[r.attending] = (acc[r.attending] || 0) + 1;
      acc.total += 1;
      acc.guests += r.guests || 0;
      return acc;
    },
    { total: 0, guests: 0 }
  );

  return (
    <div data-testid="admin-rsvps-panel">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl text-ivory">RSVPs</h2>
        <div className="flex gap-3">
          <button onClick={load} className="btn-ghost-gold" data-testid="admin-rsvps-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={exportCsv} className="btn-gold" data-testid="admin-rsvps-export">
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total responses" value={counts.total} />
        <Stat label="Joyful yes" value={counts.yes || 0} />
        <Stat label="Maybe" value={counts.maybe || 0} />
        <Stat label="Head count" value={counts.guests} />
      </div>

      {loading ? (
        <p className="text-ivory-muted">Loading…</p>
      ) : rsvps.length === 0 ? (
        <p className="text-ivory-muted italic">No RSVPs yet.</p>
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="text-[0.6rem] uppercase tracking-[0.2em] text-gold/80 border-b border-gold/20">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Guests</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Note</th>
              </tr>
            </thead>
            <tbody>
              {rsvps.map((r, i) => (
                <tr key={r.id} className="border-b border-gold/10 last:border-0 align-top" data-testid={`admin-rsvp-${i}`}>
                  <td className="px-4 py-3 text-ivory font-serif text-base">{r.name}</td>
                  <td className="px-4 py-3 text-ivory-muted text-xs">
                    {r.email && <div className="flex items-center gap-1"><Mail size={11} />{r.email}</div>}
                    {r.phone && <div className="flex items-center gap-1 mt-1"><Phone size={11} />{r.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`uppercase text-[0.6rem] tracking-[0.2em] px-2 py-1 rounded-full border ${
                      r.attending === "yes" ? "text-gold border-gold/40" :
                      r.attending === "maybe" ? "text-ivory-muted border-gold/20" :
                      "text-burgundy-glow border-burgundy/40"
                    }`}>{r.attending}</span>
                  </td>
                  <td className="px-4 py-3 text-ivory"><Users size={12} className="inline mr-1" />{r.guests}</td>
                  <td className="px-4 py-3 text-ivory-muted capitalize">{r.event}</td>
                  <td className="px-4 py-3 text-ivory-muted italic max-w-xs">{r.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MessagesPanel() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setMessages(await getMessages()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await axios.delete(`${API}/messages/${id}`, { headers: { "x-admin-key": getAdminKey() } });
    setMessages(messages.filter((m) => m.id !== id));
  };

  return (
    <div data-testid="admin-messages-panel">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl text-ivory">Guest Messages</h2>
        <button onClick={load} className="btn-ghost-gold" data-testid="admin-messages-refresh">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      {loading ? (
        <p className="text-ivory-muted">Loading…</p>
      ) : messages.length === 0 ? (
        <p className="text-ivory-muted italic">No messages yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {messages.map((m, i) => (
            <div key={m.id} className="glass-card p-5" data-testid={`admin-message-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-serif text-lg text-ivory flex items-center gap-2">
                  <MessageSquare size={14} className="text-gold" /> {m.name}
                </p>
                <button
                  onClick={() => remove(m.id)}
                  className="text-burgundy-glow hover:text-gold p-1"
                  aria-label="Delete message"
                  data-testid={`admin-message-delete-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-ivory-muted whitespace-pre-line">{m.message}</p>
              <p className="mt-2 text-[0.6rem] uppercase tracking-[0.3em] text-gold/60">
                {new Date(m.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass-card p-4 text-center">
      <p className="text-[0.6rem] uppercase tracking-[0.3em] text-ivory-dim">{label}</p>
      <p className="mt-1 font-serif text-3xl gold-text">{value}</p>
    </div>
  );
}
